//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

// #region Imports
import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, ethers, Signer } from 'ethers'

import { ActivityEnum, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { Erc20Abi, PoolAbi } from '@shared/Abis'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { ERC20Wei, otAssert } from '@shared/conversions'

import { ProviderReadOnly } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'

export const approveDepositToPoolTx =
  (props: { poolAddr: EthAddr; amountWei: ERC20Wei }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const activity = ActivityEnum.POOL_DEPOSIT_APPROVE
    const { poolAddr, amountWei } = props
    try {
      const { webSocket, userConnection } = getState().chainState

      logOT.info(`--- userConnection`, userConnection)
      // logOT.info(`--- userConnection2`, userConnection)

      const { chainConfigurationName, providerReadOnly } = getState().chainState.chainConfiguration
      otAssert(providerReadOnly, 'providerReadOnly is not defined')
      otAssert(userConnection, 'userConnection is not defined')
      const { accountAddr, roleEnum, signer } = userConnection
      assertRole(roleEnum, RoleEnum.LENDER)

      logOT.debug('pooApproveDepositTx ', accountAddr, poolAddr, amountWei)

      await approveDepositToPool(
        webSocket,
        signer as Signer,
        providerReadOnly,
        poolAddr,
        amountWei,
        chainConfigurationName
      )

      dispatch(poolActions.approveDepositToPoolSuccess({ poolAddr, amountWei }))
    } catch (e: any) {
      dispatch(poolActions.depositToPoolFail({ poolAddr, amountWei }))

      await dispatchError(dispatch, e, activity, 1013)
    }
  }

export const approveDepositToPool = async (
  webSocket: WebSocket | undefined,
  lenderSigner: Signer,
  providerReadOnly: ProviderReadOnly,
  poolAddr: EthAddr,
  depositAmountWei: ERC20Wei,
  chainConfigurationName: string
): Promise<ethers.ContractTransactionResponse | undefined> => {
  let approveReceipt: ethers.ContractTransactionResponse | undefined = undefined
  const uuid = txUUID()
  webSocket?.send(
    wsServerActionPoolTXRequest(ActivityEnum.POOL_DEPOSIT_APPROVE, poolAddr, uuid, chainConfigurationName)
  )

  logOT.debug('approveDepositToPool 1 ', depositAmountWei)
  const lenderAddr: EthAddr = await lenderSigner.getAddress()
  const poolContract = new Contract(poolAddr, PoolAbi, providerReadOnly)

  const liquidityAsset = await poolContract.liquidityAssetAddr()
  const erc20Contract = new Contract(liquidityAsset, Erc20Abi, providerReadOnly)
  const currentAllowance = await erc20Contract.allowance(lenderAddr, poolAddr)
  logOT.debug('approveDepositToPool 3 currentAllowance', currentAllowance.toString())

  const approveAmount = depositAmountWei

  logOT.debug('approveDepositToPool 4 currentAllowance', approveAmount, depositAmountWei, approveAmount.toString())

  const signerRpcAddress = await lenderSigner.provider?.getNetwork()
  logOT.debug('approveDepositToPool signer RPC address', signerRpcAddress)

  const erc20ContractSigned = new Contract(liquidityAsset, Erc20Abi, lenderSigner)
  const _approveReceipt = await erc20ContractSigned.approve(poolAddr, approveAmount)
  approveReceipt = _approveReceipt

  webSocket?.send(
    wsServerActionPoolTX(
      ActivityEnum.POOL_DEPOSIT_APPROVE,
      poolAddr,
      uuid,
      approveReceipt?.hash as TxHash,
      chainConfigurationName
    )
  )
  await _approveReceipt?.wait(1)

  logOT.debug('approveDepositToPool approveReceipt', approveReceipt)
  return approveReceipt
}
